import moment from 'moment';

const pickFirst = (source, keys) => {
  for (const key of keys) {
    if (Object.prototype.hasOwnProperty.call(source, key)) {
      const value = source[key];
      if (value !== undefined && value !== null && value !== '') {
        return value;
      }
    }
  }
  return undefined;
};

export const buildCanonicalPrefill = (source = {}) => {
  const prefill = {};

  const mappings = [
    ['patient_first_name', ['patient_first_name', 'patientFirstName']],
    ['patient_middle_name', ['patient_middle_name', 'patientMiddleName']],
    ['patient_last_name', ['patient_last_name', 'patientLastName']],
    ['patient_dob', ['patient_dob', 'patientDOB', 'patientDob']],
    ['patient_phone_number', ['patient_phone_number', 'patient_phone', 'patientPhoneNumber']],
    ['patient_gender', ['patient_gender', 'patientGender']],
    ['patient_email', ['patient_email', 'patientEmail']],
    ['patient_address', ['patient_address', 'patientAddress']],
    ['patient_city', ['patient_city', 'patientCity']],
    ['patient_state', ['patient_state', 'patientState']],
    ['patient_zip_code', ['patient_zip_code', 'patient_zip', 'patientZipCode']],
    ['patient_identifier', ['patient_identifier', 'patient_mrn', 'patientMrn', 'mrn']],
    ['mrn', ['mrn', 'patient_mrn', 'patientMrn', 'patient_identifier']],
    ['document_category', ['document_category', 'documentCategory']],
    ['document_title', ['document_title', 'documentTitle', 'fileName']],
    ['date_of_service', ['date_of_service', 'dateOfService']],
    ['notes', ['notes', 'documentNotes']],
    ['insurance_primary_payer', ['primary_plan', 'payer_name', 'insurance_primary_payer', 'primaryPlan', 'payerName']],
    ['insurance_primary_member_id', ['primary_member_id', 'insurance_primary_member_id', 'primaryMemberId']],
    ['insurance_policy_type', ['policy_type', 'insurance_policy_type']],
    ['insurance_group_number', ['group_number', 'insurance_group_number', 'groupNumber']],
    ['insurance_effective_date', ['insurance_effective_date']],
    ['insurance_secondary_payer', ['secondary_payer_name', 'insurance_secondary_payer', 'secondaryPayerName']],
    ['insurance_secondary_member_id', ['secondary_member_id', 'insurance_secondary_member_id', 'secondaryMemberId']],
    ['insurance_secondary_group_number', ['secondary_group_number', 'insurance_secondary_group_number', 'secondaryGroupNumber']],
    ['referring_provider', ['referring_provider_id', 'referring_provider', 'referringProvider']],
    ['referring_provider_name', ['referring_provider_name', 'referring_provider', 'referringProvider']],
    ['provider_office', ['provider_office', 'referring_provider_office', 'referringProviderOffice']],
  ];

  mappings.forEach(([canonicalKey, candidates]) => {
    const value = pickFirst(source, candidates);
    if (value !== undefined) {
      prefill[canonicalKey] = value;
      // Also emit under the first candidate key (the primary field name) so
      // UI schemas whose internal_name differs from the canonical key can
      // find the value (e.g. internal_name "primary_plan" vs canonical
      // "insurance_primary_payer").
      // Note: this also affects referring_provider (emits referring_provider_id),
      // which is harmless — no UI schema uses that key as internal_name.
      const primaryKey = candidates[0];
      if (primaryKey !== canonicalKey && !prefill[primaryKey]) {
        prefill[primaryKey] = value;
      }
    }
  });

  if (source.location_id) {
    prefill.location = source.location_id;
  }
  if (source.referring_provider_id) {
    prefill.referring_provider = source.referring_provider_id;
  }

  return prefill;
};

// Whether a form value counts as filled. Shared by the drawer and the NextGen
// metadata editor so "is this field answered?" cannot drift between the control
// that collects the value and the code that decides whether to submit it.
export const hasPresentValue = (value) => value !== undefined
  && value !== null
  && String(value).trim() !== '';

// The spellings a prefilled date may arrive in. `buildCanonicalPrefill` above
// carries values extracted from a document, so the source is free text.
export const PREFILL_DATE_FORMATS = [moment.ISO_8601, 'MM/DD/YYYY', 'YYYY-MM-DD'];

// Parses a prefilled date to a moment, or null when it is not a date at all.
// Strict on purpose: moment's fallback parser reads "eff. 3/1 (see card)" as a
// real date, and an invalid moment is truthy and formats to the literal string
// "Invalid date" — so a loose parse either invents a value the document never
// contained or smuggles a non-date downstream. Both reach the EHR.
export const parsePrefillDate = (value) => {
  if (moment.isMoment(value)) return value.isValid() ? value : null;
  if (!hasPresentValue(value)) return null;

  const parsed = moment(value, PREFILL_DATE_FORMATS, true);
  return parsed.isValid() ? parsed : null;
};

export const normalizePhoneDigits = (value) => {
  if (value === undefined || value === null || value === '') {
    return '';
  }

  let digits = String(value).replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) {
    digits = digits.slice(1);
  }

  return digits.slice(0, 10);
};

export const validateCrossFieldRules = (validations, values) => {
  if (!Array.isArray(validations) || !validations.length) return [];

  const errors = [];
  validations.forEach((rule) => {
    if (rule.type === 'one_of_required') {
      const filled = (rule.fields || []).some((field) => {
        const v = values?.[field];
        return v !== undefined && v !== null && v !== '';
      });

      if (!filled) {
        errors.push({ fields: rule.fields, message: rule.message });
      }
    }

    if (rule.type === 'conditional_required') {
      const conditionValue = values?.[rule.condition_field];
      const requiredValue = values?.[rule.required_field];
      if (conditionValue && (!requiredValue || requiredValue === '')) {
        errors.push({ fields: [rule.required_field], message: rule.message });
      }
    }
  });

  return errors;
};

export const normalizeFormValues = (fields, values) => {
  const output = {};
  fields.forEach(field => {
    const {
      internal_name: name,
      component,
      input_mask: inputMask,
    } = field;
    const value = values[name];

    if (value === undefined) {
      return;
    }

    if (component === 'date' && value) {
      output[name] = value.format('YYYY-MM-DD');
    } else if (component === 'multi_select' && Array.isArray(value)) {
      // An assignee picker collects scalar EMR user ids, but the upload API stores
      // assignees as { type, emr_id } objects (see EmrUpload#task_assignees_shape). When the
      // field declares assignee_type, wrap each selected id with it; otherwise pass the
      // scalar array through unchanged.
      if (field.assignee_type) {
        // Single pass: drop blanks and wrap each selected scalar id in the same flatMap.
        output[name] = value.flatMap((entry) => (
          entry !== undefined && entry !== null && String(entry).trim() !== ''
            ? [{ type: field.assignee_type, emr_id: String(entry) }]
            : []
        ));
      } else {
        output[name] = value;
      }
    } else if (inputMask === 'phone') {
      output[name] = normalizePhoneDigits(value);
    } else {
      output[name] = value;
    }
  });
  return output;
};
